import * as React from 'react';
import { SpinnerWrapper, CustomButton, ButtonType, AlertMessage, AlertType, PaginationNavigation } from "soprasteria-ui-library";
import { IInvoice, IInvoiceRow } from '../../../models/IInvoice';
import { InvoiceInformation } from './InvoiceInformation';
import { InvoiceRowsTable } from './InvoiceRowsTable';
import { useParams } from 'react-router';
import { useAuth } from '../../../App';
import { InvoiceApi } from '../../../api/InvoiceApi';
import { InvoiceAdministrationButtons } from './InvoiceAdministration/InvoiceAdministrationButtons';
import { ApproveInvoiceModal } from './InvoiceModals/ApproveInvoiceModal'
import { AttestInvoiceModal } from './InvoiceModals/AttestInvoiceModal'
import { setMessage } from '../../../helpers/MessageHelper';
import { IPage } from '../../../models/IPage';
import { IPagination } from 'soprasteria-ui-library/dist/models/IPagination';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { LIBRETTOADMIN } from '../../../utils/RoleConstants';

export const Invoice: React.FunctionComponent<{}> = () => {

    const auth = useAuth();
    const api = new InvoiceApi(auth);
    const userInfo = React.useContext(UserContext);

    const [invoice, setInvoice] = React.useState<IInvoice>();
    const { invoiceId } = useParams<{invoiceId: string}>();    
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);
    const [isFetchingInvoice, setIsFetchingInvoice] = React.useState<boolean>(false);
    const [isFetchingInvoiceRows, setIsFetchingInvoiceRows] = React.useState<boolean>(false);
    const [invoiceRows, setInvoiceRows] = React.useState<IInvoiceRow[]>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const hasRoleSysAdmin = isInRole(LIBRETTOADMIN, userInfo.roles ? userInfo.roles : []);

    const pageSize = 10;
    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const [pagingParams, setPagingParams] = React.useState<IPage>();

    React.useEffect(() => {
        getInvoice();
        getInvoiceRows({ pageSize: pageSize, pageNumber: 1 });
    }, [])

    const getInvoice = () => {
        if (invoiceId) {
            setIsFetchingInvoice(true);
            api.getInvoice(parseInt(invoiceId), (data) => {
                setInvoice(data);
            }, () => setIsFetchingInvoice(false));
        }
    }

    const getInvoiceRows = (data: IPage) => {
        if (invoiceId) {
            setIsFetchingInvoiceRows(true);
            api.getInvoiceRows(parseInt(invoiceId), data, (response) => {
                setInvoiceRows(response.data);
                setPaginationHeader(response.paginationHeader);
            }, () => {
                setIsFetchingInvoiceRows(false);
            });

            setPagingParams(data);
        }
    }

    const onPageSelect = (pageNumber: number) => {
        if (pagingParams) {
            var currentPagingParams = pagingParams;
            currentPagingParams.pageNumber = pageNumber;

            getInvoiceRows(currentPagingParams);
        }
    }

    const exportInvoiceToExcel = () => {
        setIsExportingToExcel(true);
        if (invoiceId) {
            api.getInvoiceExcel(parseInt(invoiceId), undefined, () => setIsExportingToExcel(false));
        }
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    return (
        <SpinnerWrapper isLoading={isFetchingInvoice || isFetchingInvoiceRows}>
            <section className="card" >
                <div className="card-header" >
                    <div className="row">
                        <div className="col">
                            <h1>Faktura - {invoice?.invoiceType}</h1>
                        </div>
                        <div className="col text-right">
                            <CustomButton onClick={exportInvoiceToExcel} buttonType={ButtonType.excel} buttonText="Exportera till excel" isLoading={isExportingToExcel} />
                        </div>
                    </div>
                </div>
                <div className="card-body row">
                    <div className="col-12">
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                    </div>
                    {
                        invoice ?
                            <>
                                <InvoiceInformation
                                    isSysAdmin={hasRoleSysAdmin}
                                    invoiceVerNr={invoice.invoiceVerNr}
                                    invoiceStatus={invoice.status}
                                    invoiceDate={invoice.invoiceDate}
                                    invoiceDueDate={invoice.invoiceDueDate}
                                    invoiceAmount={invoice.amount}
                                    umbrellaOrganisation={invoice.careProvider.organisationName!}
                                    orgNumber={invoice.careProvider.orgNum!}
                                    careProviderName={invoice.careProvider.name}
                                    careProviderNumber={invoice.careProvider.clinicNumber!}
                                    invoiceReference={invoice.invoiceReference}
                                    createdByInformation={invoice.createdByInformation}
                                    approvedByInformation={invoice.approvedByInformation}
                                    attestedByInformation={invoice.attestedByInformation}
                                    exportedDate={invoice.exportedDate}
                                    numberOfUniquePatients={invoice.numberOfUniquePatients}
                                />
                            </>
                            : null
                    }
                    {
                        invoice && invoiceRows ?
                            <>
                                <div className="col-12">
                                    <InvoiceRowsTable invoiceRows={invoiceRows} />
                                    <PaginationNavigation
                                        ariaLabel="invoice rows pagination selector"
                                        pagination={paginationHeader}
                                        pageNeighbours={2}
                                        selectedPage={page => onPageSelect(page)}
                                        showCount={true} />
                                </div>
                            </>
                            : null
                    }
                </div>
                <div className="card-footer">
                    <InvoiceAdministrationButtons canApproveInvoice={invoice?.canApproveInvoice} canAttestInvoice={invoice?.canAttestInvoice} />
                </div>
            </section>
            {
                invoiceId && invoice?.canApproveInvoice ?
                    <ApproveInvoiceModal getInvoice={getInvoice} invoiceId={parseInt(invoiceId)} changeSuccessMessage={changeSuccessMessage} />
                    : null
            }
            {
                invoiceId && invoice?.canAttestInvoice ?
                    <AttestInvoiceModal getInvoice={getInvoice} invoiceId={parseInt(invoiceId)} changeSuccessMessage={changeSuccessMessage} />
                    : null
            }
        </SpinnerWrapper>
    )
}

